import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import type { TechnicalLogEntryDto } from '../../models/technical-log-entry.dto';
import type { ITechnicalLogStoreProvider } from './i-technical-log-store.provider';

const DATABASE_NAME = 'dxp-technical-logs';
const DATABASE_VERSION = 1;
const STORE_NAME = 'entries';
const TIMESTAMP_INDEX = 'at';

/**
 * Il diario tecnico in `indexedDB`. Sta qui e non in `localStorage` per due ragioni misurabili: la
 * scrittura e' asincrona — una riga di log non ferma il thread che disegna la chat, e se ne scrive una a
 * ogni chiamata di rete — e la quota e' di un altro ordine rispetto ai ~5MB che `localStorage` divide gia'
 * con tema, lingua e consenso.
 *
 * Niente politica: scadenze e finestre le passa l'Handler. Qui c'e' solo la forma dell'archivio.
 */
@Injectable()
export class IndexedDbTechnicalLogStoreProvider implements ITechnicalLogStoreProvider {
  private readonly document = inject(DOCUMENT);

  /**
   * L'apertura, tentata una volta sola. `null` quando l'archivio non c'e': finestra privata, quota
   * esaurita, storage bloccato dal browser. Non e' un errore da propagare — la pagina deve funzionare
   * anche senza diario, e chi registra non ha niente da fare con quella notizia.
   */
  private connection?: Promise<IDBDatabase | null>;

  /**
   * Gli intervalli di chiave si prendono dalla finestra del documento come il database, e non dal
   * globale: sono la stessa implementazione di `indexedDB`, e prenderne una da un posto e una dall'altro
   * significherebbe passare a un archivio finto gli intervalli di quello vero.
   */
  private get keyRange(): typeof IDBKeyRange | undefined {
    return this.document.defaultView?.IDBKeyRange;
  }

  async append(entries: readonly TechnicalLogEntryDto[]): Promise<void> {
    if (entries.length === 0) return;

    const database = await this.open();
    if (!database) return;

    await this.inTransaction(database, 'readwrite', (store) => {
      for (const entry of entries) store.add(entry);
    });
  }

  async readSince(fromEpochMs: number): Promise<readonly TechnicalLogEntryDto[]> {
    const database = await this.open();
    if (!database) return [];

    const range = this.keyRange;
    if (!range) return [];

    try {
      return await new Promise<readonly TechnicalLogEntryDto[]>((resolve) => {
        const request = database
          .transaction(STORE_NAME, 'readonly')
          .objectStore(STORE_NAME)
          .index(TIMESTAMP_INDEX)
          // L'indice e' sul momento, quindi il risultato arriva gia' in ordine di tempo: nessun
          // ordinamento in memoria su una finestra che puo' contare migliaia di righe.
          .getAll(range.lowerBound(fromEpochMs));

        request.onsuccess = () => resolve(request.result as TechnicalLogEntryDto[]);
        request.onerror = () => resolve([]);
      });
    } catch {
      return [];
    }
  }

  async deleteBefore(epochMs: number): Promise<void> {
    const database = await this.open();
    if (!database) return;

    const range = this.keyRange;
    if (!range) return;

    await this.inTransaction(database, 'readwrite', (store) => {
      // Per intervallo sull'indice e non riga per riga: la pulizia gira all'avvio, davanti a chi sta
      // aspettando la pagina.
      store.index(TIMESTAMP_INDEX).openKeyCursor(range.upperBound(epochMs, true)).onsuccess = (
        event,
      ) => {
        const cursor = (event.target as IDBRequest<IDBCursor | null>).result;
        if (!cursor) return;

        store.delete(cursor.primaryKey);
        cursor.continue();
      };
    });
  }

  private open(): Promise<IDBDatabase | null> {
    this.connection ??= this.openOnce();

    return this.connection;
  }

  private openOnce(): Promise<IDBDatabase | null> {
    // `indexedDB` si raggiunge dalla finestra del documento iniettato e mai dal globale, cosi' il
    // confine resta sostituibile nei test come ogni altro Provider.
    const factory = this.document.defaultView?.indexedDB;
    if (!factory) return Promise.resolve(null);

    return new Promise<IDBDatabase | null>((resolve) => {
      let request: IDBOpenDBRequest;

      try {
        request = factory.open(DATABASE_NAME, DATABASE_VERSION);
      } catch {
        // Un browser che nega lo storage solleva gia' qui, prima di ogni evento.
        resolve(null);
        return;
      }

      request.onupgradeneeded = () => {
        const database = request.result;
        if (database.objectStoreNames.contains(STORE_NAME)) return;

        const store = database.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });
        store.createIndex(TIMESTAMP_INDEX, 'at');
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
  }

  private inTransaction(
    database: IDBDatabase,
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => void,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      let transaction: IDBTransaction;

      try {
        transaction = database.transaction(STORE_NAME, mode);
      } catch {
        resolve();
        return;
      }

      transaction.oncomplete = () => resolve();
      // Un archivio pieno o una transazione interrotta non sono notizie per chi registra: la riga si
      // perde, la pagina prosegue.
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();

      try {
        work(transaction.objectStore(STORE_NAME));
      } catch {
        resolve();
      }
    });
  }
}
